import _ from 'lodash';
import {useMemo} from 'react';
import {Edge} from 'reactflow';

import {DecisionTreeNode} from '../../../../../libs/shared/core/src/helpers/types/decisionTreeTypes';
import {ChannelTypesEnum} from '../../../../../libs/shared/core/src/models/api/channelCommonTypes';
import {ChannelModel} from '../../../../../libs/shared/core/src/models/api/channelModel';
import {DecisionTreeModel} from '../../../../../libs/shared/core/src/models/api/decisionTrees/decisionTreeModel';
import {
  DecisionTreeNodeDataOnlineHoursFrontChat,
  DecisionTreeNodeDataSemanticSearchFrontChat,
  DecisionTreeNodeDataSubtreeFrontChat,
  isOnlineHoursNodeData,
  isSemanticSearchNodeData,
} from '../../../../../libs/shared/core/src/models/api/decisionTrees/decisionTreeNodeDataFrontChatModel';
import {
  gatherNodeDescendantIds,
  isSubtreeNode,
} from '../../../../../libs/shared/ui/src/components/decisionTrees/decisionTreeBuilder/helpers';
import {gatherSubtreeExitNodes, OnlineHoursOptionsEnum, SemanticSearchOptionsEnum} from '../helpers';
import {ChatBotBuilderErrorReasonsEnum, ChatBotBuilderNode} from '../types';

/*
 * Constants.
 */

// A stable reference for the empty `disabledNodeIds` state is needed to prevent unnecessary re-renders.
const emptyDisabledNodeIds: ReadonlyArray<string> = Object.freeze([]);

/*
 * Hooks.
 */

export function useDisabledNodeIds(
  nodes: ReadonlyArray<ChatBotBuilderNode>,
  edges: ReadonlyArray<Edge>,
  channel: ChannelModel | undefined,
  errorReason: ChatBotBuilderErrorReasonsEnum | undefined,
  subtrees: ReadonlyArray<DecisionTreeModel>,
): ReadonlyArray<string> {
  // Computes all disabled nodes based on if they are in a path that contains a semantic search node.
  const disabledSemanticSearchNodeIds = useMemo(() => {
    if (errorReason !== ChatBotBuilderErrorReasonsEnum.IS_MISSING_AI_ANSWERS) {
      return emptyDisabledNodeIds;
    }

    // Find all the semantic search nodes.
    const invalidNodeIds = new Set<string>([]);
    const semanticSearchNodes: ReadonlyArray<DecisionTreeNode<DecisionTreeNodeDataSemanticSearchFrontChat>> =
      _(nodes)
        .filter((node) => isSemanticSearchNodeData(node.data))
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        .map((node) => node as DecisionTreeNode<DecisionTreeNodeDataSemanticSearchFrontChat>)
        .compact()
        .value();

    // If there is no semantic search node, then there is no invalid node.
    if (semanticSearchNodes.length === 0) {
      return emptyDisabledNodeIds;
    }

    for (const semanticSearchNode of semanticSearchNodes) {
      // Get all the handle for the "No" path as all other paths are ignored.
      const ignoredNotHandledSourceIds = _(semanticSearchNode.data.options)
        .filter((option) => option.value === SemanticSearchOptionsEnum.NO)
        .map((option) => option.sourceHandle)
        .value();
      const data = gatherNodeDescendantIds(
        semanticSearchNode.id,
        [...nodes],
        [...edges],
        ignoredNotHandledSourceIds,
      );
      // We do not want to add the node itself to the invalid list as it is not fully
      // invalid unless it is already in a chain of invalid nodes.
      data.nodeIds.forEach((nodeId) =>
        nodeId !== semanticSearchNode.id ? invalidNodeIds.add(nodeId) : undefined,
      );
    }

    return Array.from(invalidNodeIds);
  }, [errorReason, nodes, edges]);

  // Computes all disabled nodes based on if they are in the "Outside of online hours" path of an online hours node with an error.
  const disabledOnlineHoursNodeIds = useMemo(() => {
    const onlineHoursNodes: ReadonlyArray<DecisionTreeNode<DecisionTreeNodeDataOnlineHoursFrontChat>> = _(
      nodes,
    )
      .filter((node) => isOnlineHoursNodeData(node.data))
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      .map((node) => node as DecisionTreeNode<DecisionTreeNodeDataOnlineHoursFrontChat>)
      .compact()
      .value();

    // If there is no online hours node, then there is no invalid node.
    if (onlineHoursNodes.length === 0) {
      return emptyDisabledNodeIds;
    }

    const invalidNodeIds = new Set<string>([]);
    for (const onlineHoursNode of onlineHoursNodes) {
      const isHiddenOutsideOnlineHours = Boolean(
        channel &&
          channel.type === ChannelTypesEnum.FRONT_CHAT &&
          channel.preferences.widgetOnlineHours.shouldHideWhenOutsideOnlineHours,
      );

      if (isHiddenOutsideOnlineHours) {
        // Get all the handle for the "Online" path as all other paths are ignored.
        const ignoreOnlineHoursHandles = _(onlineHoursNode.data.options)
          .filter((option) => option.value === OnlineHoursOptionsEnum.ONLINE)
          .map((option) => option.sourceHandle)
          .value();
        const data = gatherNodeDescendantIds(
          onlineHoursNode.id,
          [...nodes],
          [...edges],
          ignoreOnlineHoursHandles,
        );

        // We do not want to add the node itself to the invalid list as it is not fully
        // invalid unless it is already in a chain of invalid nodes.
        data.nodeIds.forEach((nodeId) =>
          nodeId !== onlineHoursNode.id ? invalidNodeIds.add(nodeId) : undefined,
        );
      }
    }

    return Array.from(invalidNodeIds);
  }, [channel, nodes, edges]);

  // Computes all unreachable branches in subtrees (because a branch's associated exit was deleted).
  const disabledSubtreeNodeIds = useMemo(() => {
    const subtreeNodes = nodes.filter(isSubtreeNode);

    if (subtreeNodes.length === 0) {
      return emptyDisabledNodeIds;
    }

    const invalidNodeIds = new Set<string>([]);
    subtreeNodes
      .map((subtreeNode) => computeSubtreeInvalidDescendantIds(subtreeNode, subtrees, nodes, edges))
      .flat()
      .forEach((nodeId) => invalidNodeIds.add(nodeId));

    return Array.from(invalidNodeIds);
  }, [subtrees, nodes, edges]);

  // Removes duplicates and returns the final list of disabled node IDs.
  const disabledNodeIds = useMemo(
    () =>
      Array.from(
        new Set([...disabledSemanticSearchNodeIds, ...disabledOnlineHoursNodeIds, ...disabledSubtreeNodeIds]),
      ),
    [disabledSemanticSearchNodeIds, disabledOnlineHoursNodeIds, disabledSubtreeNodeIds],
  );

  return disabledNodeIds;
}

/*
 * Helpers.
 */

function computeSubtreeInvalidDescendantIds(
  subtreeNode: DecisionTreeNode<DecisionTreeNodeDataSubtreeFrontChat>,
  subtrees: ReadonlyArray<DecisionTreeModel>,
  nodes: ReadonlyArray<ChatBotBuilderNode>,
  edges: ReadonlyArray<Edge>,
): ReadonlyArray<string> {
  const subtree = subtrees.find((decisionTree) => decisionTree.id === subtreeNode.data.decisionTreeId);

  // If the subtree has merged exits (aka only 1 option per node), options must be valid.
  if (!subtree || !subtreeNode.data.hasSplitExits) {
    return [];
  }

  const subtreeExitNodeIds = gatherSubtreeExitNodes(subtree).map((node) => node.id);

  const subtreeOptionsMissingExitNode = subtreeNode.data.options.filter((option) =>
    subtreeExitNodeIds.includes(option.exitNodeId ?? ''),
  );

  const unreachableBranchHandles = subtreeOptionsMissingExitNode.map((option) => option.sourceHandle);

  const data = gatherNodeDescendantIds(subtreeNode.id, [...nodes], [...edges], unreachableBranchHandles);

  // We do not want to add the node itself to the invalid list as it is not fully
  // invalid unless it is already in a chain of invalid nodes.
  return data.nodeIds.filter((nodeId) => nodeId !== subtreeNode.id);
}
